import { API_BASE } from '../config';
import type { Scene } from '../components/Scenes';

export type SceneLinkContextType = 'chapter' | 'storyline';

export interface SceneLinkContext {
  type: SceneLinkContextType;
  id: string;
}

export interface SceneLinkEntityRef {
  sourceType: string;
  sourceId: string;
  sourceDocumentId: string | null;
  targetType: string;
  targetId: string;
  targetDocumentId: string | null;
}

export function sceneLinkTargetType(
  contextType: SceneLinkContextType,
): 'manual_chapter' | 'storyline' {
  return contextType === 'chapter' ? 'manual_chapter' : 'storyline';
}

export function sceneDocumentIdOf(
  scene: Pick<Scene, 'id' | 'documentId'>,
  fallbackDocumentId: string,
): string {
  return scene.documentId ?? fallbackDocumentId;
}

export function isSceneLinkedToContext(
  refs: SceneLinkEntityRef[],
  sceneId: string,
  sceneDocumentId: string,
  context: SceneLinkContext,
  contextDocumentId: string = sceneDocumentId,
): boolean {
  const targetType = sceneLinkTargetType(context.type);
  return refs.some((ref) => {
    if (
      ref.sourceType === 'scene' &&
      ref.sourceId === sceneId &&
      ref.sourceDocumentId === sceneDocumentId &&
      ref.targetType === targetType &&
      ref.targetId === context.id &&
      (ref.targetDocumentId === contextDocumentId || ref.targetDocumentId == null)
    ) {
      return true;
    }
    if (
      ref.sourceType === targetType &&
      ref.sourceId === context.id &&
      ref.sourceDocumentId === contextDocumentId &&
      ref.targetType === 'scene' &&
      ref.targetId === sceneId &&
      ref.targetDocumentId === sceneDocumentId
    ) {
      return true;
    }
    return false;
  });
}

export function getLinkableScenes(
  scenes: Scene[],
  refs: SceneLinkEntityRef[],
  contextDocumentId: string,
  context: SceneLinkContext,
): Scene[] {
  return scenes
    .filter((scene) => {
      const sceneDocumentId = sceneDocumentIdOf(scene, contextDocumentId);
      return !isSceneLinkedToContext(
        refs,
        scene.id,
        sceneDocumentId,
        context,
        contextDocumentId,
      );
    })
    .sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));
}

export function isSceneLinkedToAnyChapter(
  refs: SceneLinkEntityRef[],
  sceneId: string,
  sceneDocumentId: string,
  chapterDocumentId: string = sceneDocumentId,
): boolean {
  return refs.some((ref) => {
    if (
      ref.sourceType === 'scene' &&
      ref.sourceId === sceneId &&
      ref.sourceDocumentId === sceneDocumentId &&
      ref.targetType === 'manual_chapter' &&
      (ref.targetDocumentId === chapterDocumentId || ref.targetDocumentId == null)
    ) {
      return true;
    }
    if (
      ref.sourceType === 'manual_chapter' &&
      ref.sourceDocumentId === chapterDocumentId &&
      ref.targetType === 'scene' &&
      ref.targetId === sceneId &&
      (ref.targetDocumentId === sceneDocumentId || ref.targetDocumentId == null)
    ) {
      return true;
    }
    return false;
  });
}

export function getUnlinkedChapterScenes(
  scenes: Scene[],
  refs: SceneLinkEntityRef[],
  chapterDocumentId: string,
): Scene[] {
  return scenes
    .filter((scene) => {
      const sceneDocumentId = sceneDocumentIdOf(scene, chapterDocumentId);
      return !isSceneLinkedToAnyChapter(
        refs,
        scene.id,
        sceneDocumentId,
        chapterDocumentId,
      );
    })
    .sort((a, b) => {
      const documentCompare = (a.documentTitle ?? a.documentId ?? '').localeCompare(
        b.documentTitle ?? b.documentId ?? '',
        undefined,
        { sensitivity: 'base' },
      );
      if (documentCompare !== 0) return documentCompare;
      return (a.order ?? 0) - (b.order ?? 0);
    });
}

export function getSceneChapterDocumentIds(
  refs: SceneLinkEntityRef[],
  sceneId: string,
  sceneDocumentId: string,
): string[] {
  const documentIds = new Set<string>();
  for (const ref of refs) {
    if (
      ref.sourceType === 'scene' &&
      ref.sourceId === sceneId &&
      ref.sourceDocumentId === sceneDocumentId &&
      ref.targetType === 'manual_chapter' &&
      ref.targetDocumentId
    ) {
      documentIds.add(ref.targetDocumentId);
    }
    if (
      ref.sourceType === 'manual_chapter' &&
      ref.sourceDocumentId &&
      ref.targetType === 'scene' &&
      ref.targetId === sceneId &&
      (ref.targetDocumentId === sceneDocumentId || ref.targetDocumentId == null)
    ) {
      documentIds.add(ref.sourceDocumentId);
    }
  }
  return [...documentIds];
}

export function shouldSuggestMoveSceneAfterChapterLink(params: {
  refsBefore: SceneLinkEntityRef[];
  refsAfter: SceneLinkEntityRef[];
  sceneId: string;
  sceneDocumentId: string;
  chapterDocumentId: string;
}): boolean {
  const { refsBefore, refsAfter, sceneId, sceneDocumentId, chapterDocumentId } = params;
  if (sceneDocumentId === chapterDocumentId) {
    return false;
  }
  const beforeDocumentIds = getSceneChapterDocumentIds(refsBefore, sceneId, sceneDocumentId);
  const afterDocumentIds = getSceneChapterDocumentIds(refsAfter, sceneId, sceneDocumentId);
  return (
    beforeDocumentIds.length === 0 &&
    afterDocumentIds.length === 1 &&
    afterDocumentIds[0] === chapterDocumentId
  );
}

export function buildSceneLinkBody(
  sceneId: string,
  sceneDocumentId: string,
  context: SceneLinkContext,
  contextDocumentId: string = sceneDocumentId,
): Record<string, string> {
  return {
    sourceType: 'scene',
    sourceId: sceneId,
    sourceDocumentId: sceneDocumentId,
    targetType: sceneLinkTargetType(context.type),
    targetId: context.id,
    targetDocumentId: contextDocumentId,
  };
}

export function buildSceneUnlinkBodies(
  sceneId: string,
  sceneDocumentId: string,
  context: SceneLinkContext,
  contextDocumentId: string = sceneDocumentId,
): Array<Record<string, string>> {
  const bodies = [buildSceneLinkBody(sceneId, sceneDocumentId, context, contextDocumentId)];
  if (context.type === 'chapter') {
    bodies.push({
      sourceType: 'manual_chapter',
      sourceId: context.id,
      sourceDocumentId: contextDocumentId,
      targetType: 'scene',
      targetId: sceneId,
      targetDocumentId: sceneDocumentId,
    });
  }
  return bodies;
}

export async function createSceneLink(params: {
  projectId: string;
  token: string;
  sceneId: string;
  documentId: string;
  sceneDocumentId?: string;
  context: SceneLinkContext;
}): Promise<boolean> {
  const sceneDocumentId = params.sceneDocumentId ?? params.documentId;
  const response = await fetch(`${API_BASE}/api/projects/${params.projectId}/entity-references`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${params.token}`,
    },
    body: JSON.stringify(
      buildSceneLinkBody(
        params.sceneId,
        sceneDocumentId,
        params.context,
        params.documentId,
      ),
    ),
  });
  return response.ok;
}

export async function removeSceneLinks(params: {
  projectId: string;
  token: string;
  sceneId: string;
  documentId: string;
  sceneDocumentId?: string;
  context: SceneLinkContext;
}): Promise<boolean> {
  const sceneDocumentId = params.sceneDocumentId ?? params.documentId;
  const bodies = buildSceneUnlinkBodies(
    params.sceneId,
    sceneDocumentId,
    params.context,
    params.documentId,
  );
  const results = await Promise.all(
    bodies.map((body) =>
      fetch(`${API_BASE}/api/projects/${params.projectId}/entity-references/remove`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${params.token}`,
        },
        body: JSON.stringify(body),
      }),
    ),
  );
  return results.every((response) => response.ok);
}

export function buildSceneOutboundUnlinkBodies(
  sceneId: string,
  documentId: string,
  entity: { type: string; id: string; documentId?: string },
): Array<Record<string, string | null>> {
  const bodies: Array<Record<string, string | null>> = [
    {
      sourceType: 'scene',
      sourceId: sceneId,
      sourceDocumentId: documentId,
      targetType: entity.type,
      targetId: entity.id,
      targetDocumentId: entity.documentId ?? null,
    },
  ];
  if (entity.type === 'manual_chapter' && entity.documentId) {
    bodies.push({
      sourceType: 'manual_chapter',
      sourceId: entity.id,
      sourceDocumentId: entity.documentId,
      targetType: 'scene',
      targetId: sceneId,
      targetDocumentId: documentId,
    });
  }
  return bodies;
}

export async function removeSceneOutboundLink(params: {
  projectId: string;
  token: string;
  sceneId: string;
  documentId: string;
  entity: { type: string; id: string; documentId?: string };
}): Promise<boolean> {
  const bodies = buildSceneOutboundUnlinkBodies(
    params.sceneId,
    params.documentId,
    params.entity,
  );
  const results = await Promise.all(
    bodies.map((body) =>
      fetch(`${API_BASE}/api/projects/${params.projectId}/entity-references/remove`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${params.token}`,
        },
        body: JSON.stringify(body),
      }),
    ),
  );
  return results.every((response) => response.ok);
}
